// Visual intent detection for conversation-first rendering policy.
// Determines whether tool results should render visual cards or stay text-only.

const VISUAL_KEYWORDS = [
    "chart",
    "graph",
    "plot",
    "visuali",      // matches visualize, visualise, visualization
    "show as chart",
    "show me a chart",
    "draw a",
    "bar chart",
    "line chart",
    "pie chart",
    "table",
    "dashboard",
    "trend",
    "breakdown",
    "treemap",
];

const SUPPRESS_KEYWORDS = [
    "text only",
    "text-only",
    "no chart",
    "no table",
    "no dashboard",
    "no visual",
    "plain text",
    "just text",
    "summarize in text",
    "summarise in text",
];

export type VisualIntent = "visual" | "suppress" | "none";

/**
 * Detect visual intent from a user message.
 * Returns "visual" if the user explicitly wants visual output,
 * "suppress" if they want text-only, or "none" for no explicit preference.
 */
export function detectVisualIntent(message: string): VisualIntent {
    if (!message) return "none";
    const lower = message.toLowerCase();

    // Suppress takes priority — user explicitly wants text
    for (const kw of SUPPRESS_KEYWORDS) {
        if (lower.includes(kw)) return "suppress";
    }

    // Visual intent
    for (const kw of VISUAL_KEYWORDS) {
        if (lower.includes(kw)) return "visual";
    }

    return "none";
}

/**
 * Decide whether to render a visual card based on user intent and tool hint.
 *
 * Rules:
 * - "suppress" intent → never render visuals
 * - "visual" intent → always render visuals if available
 * - "none" intent → text-only (don't show charts/tables unless user asks)
 */
export function shouldRenderVisual(
    intent: VisualIntent,
    _suggestVisual?: boolean,
): boolean {
    if (intent === "suppress") return false;
    if (intent === "visual") return true;
    return false;
}
